import { FeatureFlagKey } from "@redotech/feature-flags-model";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { createContext, memo, type ReactNode, useContext } from "react";

let cachedFlag: boolean | null = null;
let flagPromise: Promise<boolean> | null = null;

const IntelligentInventoryContext = createContext<boolean | undefined>(
  undefined,
);

export function useIntelligentInventoryFlag(): boolean {
  return useRequiredContext(IntelligentInventoryContext);
}

function useIsRedoEmployee(): boolean {
  const user = useContext(UserContext);
  return (
    user?.email?.endsWith("@redo.com") ||
    user?.email?.endsWith("@getredo.com") ||
    false
  );
}

export const IntelligentInventoryFlagProvider = memo(
  function IntelligentInventoryFlagProvider({
    children,
  }: {
    children: ReactNode;
  }) {
    const platformRpcClient = useRequiredContext(
      RedoPlatformAuthRpcClientContext,
    );
    const isRedoEmployee = useIsRedoEmployee();

    const flagLoad = useLoad(async () => {
      if (cachedFlag !== null) {
        return cachedFlag;
      }
      if (flagPromise === null) {
        flagPromise = platformRpcClient
          .getFeatureFlag({ flagKey: FeatureFlagKey.INTELLIGENT_INVENTORY })
          .then((response) => {
            cachedFlag = response.enabled;
            return response.enabled;
          })
          .catch((error) => {
            flagPromise = null;
            throw error;
          });
      }
      return flagPromise;
    }, []);

    const enabled = isRedoEmployee || (flagLoad.value ?? false);

    return (
      <IntelligentInventoryContext.Provider value={enabled}>
        {children}
      </IntelligentInventoryContext.Provider>
    );
  },
);
